import { isNoteKind } from '@platform/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TestEnvStrip } from '../../app/shell/TestEnvStrip';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { isApiError, readCsrfToken } from '../../api/client';
import { salesApi } from '../../api/endpoints';
import { organizationQuery, queryKeys } from '../../api/queries';
import type { ApiIssue, InvoiceKind } from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { fillTemplate } from '../../lib/fillTemplate';
import { UserText } from '../../ui/Bidi';
import { Button } from '../../ui/Button';
import { ConfirmDialog } from '../../ui/Dialog';
import { Money } from '../../ui/Layout';
import { useToast } from '../../ui/Toast';
import { MockLabel } from './status';
import styles from './Sales.module.css';

export interface DocTarget {
  id: string;
  number: string | null;
  version: number;
  /** Block 11 (m-1): the document kind, so the issue confirmation words a note as «الإشعار». */
  kind?: InvoiceKind;
  receiverName?: string | null;
  cash?: boolean;
  totalAmount?: string | null;
  /**
   * WP-1b.4: the step-4 choice of the buyer's e-mail copy — only the editor's review step passes it.
   * Without it (the list's command bar, the detail toolbar) the issue sends no copy and says so.
   */
  mail?: { send: boolean; recipient: string | null };
}

const UNDO_MS = 6000;

/** Copy warnings (TX-M4) handed to the editor of the new draft. */
export const copyWarningsFor = new Map<string, ApiIssue[]>();

/** Drafts the editor opens at the review step (WP-1b.4 «افتح خطوة المراجعة»). */
export const reviewStepFor = new Set<string>();

/** The issue refusals that come from the step-4 choice (the buyer has no usable address). */
const MAIL_REFUSALS: ReadonlySet<string> = new Set(['buyer_email_missing', 'buyer_email_invalid']);

/**
 * Row and detail actions with their confirmations (M-06): issue-and-send (irreversible) and delete
 * (a discard, with a short undo window before the request is sent). Copy opens the new draft in the
 * editor (D-4); PDF downloads the document as it is now.
 */
export function useDocumentActions(
  orgId: string,
  opts: {
    onIssued?: (id: string) => void;
    /** WP-1b.4: the issue was refused for the buyer's address; nothing was issued. */
    onMailRefused?: () => void;
  } = {},
) {
  const { t } = useI18n();
  const access = useAccess(orgId);
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const org = useQuery(organizationQuery(orgId));
  const [issueTarget, setIssueTarget] = useState<DocTarget | null>(null);
  const [signTarget, setSignTarget] = useState<DocTarget | null>(null);
  const [sendTarget, setSendTarget] = useState<DocTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocTarget | null>(null);
  const [undo, setUndo] = useState<DocTarget | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'sales'] });

  const issue = useMutation({
    mutationFn: (d: DocTarget) => salesApi.issue(orgId, d.id, d.version, Boolean(d.mail?.send)),
    onSuccess: async (res) => {
      setIssueTarget(null);
      toast.success(t.editor.issued(res.number));
      await refresh();
      opts.onIssued?.(res.id);
    },
    onError: (error) => {
      setIssueTarget(null);
      toast.error(error);
      if (isApiError(error) && MAIL_REFUSALS.has(error.code)) opts.onMailRefused?.();
      void refresh();
    },
  });

  // E-seal as a separate step (WP-1b.1): sign an issued, unsigned document, then send it.
  const sign = useMutation({
    mutationFn: (d: DocTarget) => salesApi.sign(orgId, d.id),
    onSuccess: async (res) => {
      setSignTarget(null);
      toast.success(t.sales.signed(res.number ?? ''));
      await refresh();
    },
    onError: (error) => {
      setSignTarget(null);
      toast.error(error);
      void refresh();
    },
  });

  const send = useMutation({
    mutationFn: (d: DocTarget) => salesApi.submit(orgId, d.id),
    onSuccess: async () => {
      setSendTarget(null);
      toast.success(t.sales.sent);
      await refresh();
    },
    onError: (error) => {
      setSendTarget(null);
      toast.error(error);
      void refresh();
    },
  });

  const copy = useMutation({
    mutationFn: (id: string) => salesApi.copy(orgId, id),
    onSuccess: async (res) => {
      toast.success(t.sales.copied(res.number ?? ''));
      copyWarningsFor.set(res.id, res.copyWarnings);
      await refresh();
      void navigate({
        to: '/orgs/$orgId/sales/invoices/$docId/edit',
        params: { orgId, docId: res.id },
      });
    },
    onError: (error) => toast.error(error),
  });

  // Correct and resend (WP-1b.2): open the draft that supersedes a refused document. Idempotent on
  // the server, so a second press opens the same draft.
  const supersede = useMutation({
    mutationFn: (id: string) => salesApi.supersede(orgId, id),
    onSuccess: async (res) => {
      const number = res.number ?? '';
      toast.success(
        res.created ? t.sales.correctionCreated(number) : t.sales.correctionOpen(number),
      );
      copyWarningsFor.set(res.id, res.copyWarnings);
      await refresh();
      void navigate({
        to: '/orgs/$orgId/sales/invoices/$docId/edit',
        params: { orgId, docId: res.id },
      });
    },
    onError: (error) => {
      toast.error(error);
      void refresh();
    },
  });

  const pdf = useMutation({
    mutationFn: (d: { id: string; number: string | null }) =>
      salesApi.downloadPdf(orgId, d.id, `${d.number ?? 'invoice'}.pdf`),
    onSuccess: () => toast.success(t.sales.pdfReady),
    onError: (error) => toast.error(error),
  });

  const discard = useMutation({
    mutationFn: (d: DocTarget) => salesApi.discard(orgId, d.id, d.version),
    onSuccess: () => refresh(),
    onError: (error) => {
      toast.error(error);
      void refresh();
    },
  });

  // Leaving the page during the undo window commits the delete (it was confirmed).
  const pendingRef = useRef<DocTarget | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      const pending = pendingRef.current;
      if (pending) commitRef.current(pending);
    },
    [orgId],
  );

  const commitNow = (d: DocTarget) => {
    // keepalive: the request completes even when the tab is closing.
    void fetch(
      `/api/orgs/${encodeURIComponent(orgId)}/sales/documents/${encodeURIComponent(d.id)}/discard`,
      {
        method: 'POST',
        keepalive: true,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': readCsrfToken() ?? '' },
        body: JSON.stringify({ version: d.version }),
      },
    ).then(() => refresh());
  };
  const commitRef = useRef(commitNow);
  commitRef.current = commitNow;
  // Closing the tab during the undo window commits the confirmed delete.
  useEffect(() => {
    const onHide = () => {
      if (pendingRef.current) commitRef.current(pendingRef.current);
      pendingRef.current = null;
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const startDelete = (d: DocTarget) => {
    setDeleteTarget(null);
    // A second delete inside the undo window commits the first one at once (never dropped).
    if (pendingRef.current && pendingRef.current.id !== d.id) commitNow(pendingRef.current);
    setUndo(d);
    pendingRef.current = d;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      pendingRef.current = null;
      setUndo(null);
      discard.mutate(d);
    }, UNDO_MS);
  };
  const cancelDelete = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    pendingRef.current = null;
    setUndo(null);
  };

  const environment = org.data?.etaEnvironment;
  const mail = issueTarget?.mail;
  const sendsCopy = Boolean(mail?.send && mail.recipient);
  // m-1: a note's issue confirmation reads «الإشعار», not «الفاتورة».
  const issueIsNote = isNoteKind(issueTarget?.kind ?? '');
  const openReview = (id: string) => {
    setIssueTarget(null);
    reviewStepFor.add(id);
    void navigate({ to: '/orgs/$orgId/sales/invoices/$docId/edit', params: { orgId, docId: id } });
  };
  const elements = (
    <>
      <ConfirmDialog
        isOpen={issueTarget !== null}
        onOpenChange={(open) => !open && setIssueTarget(null)}
        title={issueIsNote ? t.editor.confirm.titleNote : t.editor.confirm.title}
        confirmLabel={t.editor.confirm.confirm}
        confirmVariant="primary"
        isPending={issue.isPending}
        onConfirm={() => issueTarget && issue.mutate(issueTarget)}
      >
        <TestEnvStrip compact />
        <p>{issueIsNote ? t.editor.confirm.bodyNote : t.editor.confirm.body}</p>
        <dl className={styles.kv} data-testid="issue-confirm-summary">
          <dt>{t.editor.confirm.organization}</dt>
          <dd>
            <UserText>{org.data?.legalName ?? org.data?.name}</UserText>
          </dd>
          <dt>{t.editor.confirm.environment}</dt>
          <dd>
            {environment ? t.env[environment] : '—'} <MockLabel environment={environment} />
          </dd>
          <dt>{t.editor.confirm.customer}</dt>
          <dd>{issueTarget?.cash ? t.sales.cash : (issueTarget?.receiverName ?? '—')}</dd>
          <dt>{t.editor.confirm.number}</dt>
          <dd className={styles.mono}>{issueTarget?.number}</dd>
          <dt>{t.editor.confirm.total}</dt>
          <dd>
            <Money value={issueTarget?.totalAmount} currency="EGP" testId="issue-confirm-total" />
          </dd>
          {/* WP-1b.4: the step-4 choice as one sentence; never a control here (M-5). */}
          <dt>{t.editor.mail.title}</dt>
          <dd data-testid="issue-confirm-mail" data-mail={sendsCopy ? 'send' : 'none'}>
            {sendsCopy ? (
              <>
                {fillTemplate(
                  t.editor.confirm.mailSend,
                  <bdi dir="ltr" className={styles.mono}>
                    {mail?.recipient}
                  </bdi>,
                )}
                {org.data?.emailTransport === 'file' ? ` (${t.editor.confirm.mailTest})` : null}
              </>
            ) : (
              t.editor.confirm.mailNone
            )}
          </dd>
        </dl>
        {issueTarget && !mail ? (
          // From the list's command bar or the detail toolbar: no copy; the choice is on step 4.
          <div className={styles.confirmMailNote}>
            <p>{t.editor.confirm.mailFromEditor}</p>
            {access.can('invoice.edit') ? (
              <Button
                variant="ghost"
                size="sm"
                className={styles.textButton}
                onPress={() => openReview(issueTarget.id)}
                testId="issue-confirm-mail-review"
              >
                {t.editor.confirm.openReview}
              </Button>
            ) : null}
          </div>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={signTarget !== null}
        onOpenChange={(open) => !open && setSignTarget(null)}
        title={t.sales.signConfirmTitle}
        confirmLabel={t.sales.signConfirmLabel}
        confirmVariant="primary"
        isPending={sign.isPending}
        onConfirm={() => signTarget && sign.mutate(signTarget)}
      >
        <p>{t.sales.signConfirmBody}</p>
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={sendTarget !== null}
        onOpenChange={(open) => !open && setSendTarget(null)}
        title={t.sales.sendConfirmTitle}
        confirmLabel={t.sales.sendConfirmLabel}
        confirmVariant="primary"
        isPending={send.isPending}
        onConfirm={() => sendTarget && send.mutate(sendTarget)}
      >
        <p>{t.sales.sendConfirmBody}</p>
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t.detail.deleteTitle}
        confirmLabel={t.detail.deleteConfirm}
        confirmVariant="danger"
        onConfirm={() => deleteTarget && startDelete(deleteTarget)}
      >
        <p>{t.detail.deleteBody(deleteTarget?.number ?? '')}</p>
      </ConfirmDialog>
      {undo ? (
        <div className={styles.undoBar} role="status" data-testid="undo-bar">
          <span>{t.sales.deleted(undo.number ?? '')}</span>
          <button type="button" onClick={cancelDelete} data-testid="undo-delete">
            {t.sales.undo}
          </button>
        </div>
      ) : null}
    </>
  );

  return {
    elements,
    requestIssue: (d: DocTarget) => setIssueTarget(d),
    requestSign: (d: DocTarget) => setSignTarget(d),
    requestSend: (d: DocTarget) => setSendTarget(d),
    requestDelete: (d: DocTarget) => setDeleteTarget(d),
    copy: (id: string) => copy.mutate(id),
    supersede: (id: string) => supersede.mutate(id),
    isSuperseding: supersede.isPending,
    pdf: (d: { id: string; number: string | null }) => pdf.mutate(d),
    isBusy:
      copy.isPending ||
      supersede.isPending ||
      pdf.isPending ||
      issue.isPending ||
      discard.isPending ||
      sign.isPending ||
      send.isPending,
    pendingDeleteId: undo?.id ?? null,
  };
}
